import "common/Charts/chartRegistry";
import React, {
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { css, useTheme } from "styled-components";
import { Doughnut } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import { LanguageNameSpaces, PieChartDataPoint } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { ALLOCATION_TOTAL_ID, MOBILE_ALLOCATION_ID } from "testIds";
import useMedia from "utils/hooks/useMedia";
import { getNewLookTokens } from "common/NewLook/tokens.ts";
import Tooltip from "common/Tooltips/Tooltip";
import Surface from "common/NewLook/Surface.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  getTooltipGenerator,
  pieChartTooltipInnerHtmlGenerator,
} from "common/Charts/tooltipGenerator.jsx";
import { DEFAULT_CHART_TOOLTIP_MARGIN } from "common/constants";

interface AllocationDonutProps {
  title?: ReactNode;
  /** Shown on the right of the title. */
  action?: ReactNode;
  data: PieChartDataPoint[];
  total: ReactNode;
  loading?: boolean;
  /** Unique DOM id for the chart and its tooltip. */
  chartId: string;
  /** Items listed by name; the rest are summed into one "Other" slice. */
  maxItems?: number;
  /** Smaller donut, for phones and narrow cards. */
  compact?: boolean;
  /** Renders without the card border and padding. */
  bare?: boolean;
  /** Only the ring (the caller lays out its own legend). */
  hideLegend?: boolean;
  /** Shown beside the empty ring when nothing is allocated. */
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
}

const DONUT_SIZE = 140;
const COMPACT_DONUT_SIZE = 112;
const RING_WIDTH = 5;
const COMPACT_RING_WIDTH = 4;
const CHART_PADDING = 4;

const roundPercent = (value: number) => Math.round(value * 100) / 100;

/**
 * The new look's allocation card: a slim donut beside a short legend. The
 * content sits in the middle of the card, so a card stretched by its row keeps
 * even space above and below.
 */
const AllocationDonut = ({
  title,
  action,
  data,
  total,
  loading,
  chartId,
  maxItems = 3,
  compact,
  bare,
  hideLegend,
  emptyTitle,
  emptyDescription,
}: AllocationDonutProps) => {
  const theme = useTheme();
  const isPhone = useMedia(["(max-width: 600px)"], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const [expanded, setExpanded] = useState(false);
  const size = compact ? COMPACT_DONUT_SIZE : DONUT_SIZE;

  const items = useMemo(() => {
    // Up to maxItems + 1 rows: the biggest by name, then one "Other".
    if (data.length <= maxItems + 1) return data;
    const rest = data.slice(maxItems);
    const other: PieChartDataPoint = {
      name: t("labels.other"),
      value: roundPercent(rest.reduce((sum, item) => sum + item.value, 0)),
      color: theme.colors.lightestText,
    };
    return [...data.slice(0, maxItems), other];
  }, [data, maxItems, t, theme]);

  const hasData = items.some((item) => item.value > 0);
  // The first load shows the card's shape; a refetch keeps the data.
  const pending = !!loading && !hasData;

  const ringWidth = compact ? COMPACT_RING_WIDTH : RING_WIDTH;

  const chart = useMemo(() => {
    const radius = size / 2 - CHART_PADDING;
    const chartData = {
      datasets: [
        {
          data: hasData ? items.map((item) => item.value) : [1],
          backgroundColor: hasData
            ? items.map((item) => item.color)
            : [theme.colors.cardLightBgColor],
          borderWidth: 0,
          spacing: hasData && items.length > 1 ? 2 : 0,
          borderRadius: 1,
          hoverOffset: hasData ? 3 : 0,
          rawData: items,
        },
      ],
    };
    const options: ChartOptions<"doughnut"> = {
      cutout: `${((radius - ringWidth) / radius) * 100}%`,
      maintainAspectRatio: false,
      responsive: true,
      layout: { padding: CHART_PADDING },
      plugins: {
        legend: { display: false },
        // Other charts register chartjs-plugin-datalabels globally; the
        // donut never shows values on its ring.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...({ datalabels: { display: false } } as any),
        tooltip: {
          enabled: false,
          external: hasData
            ? getTooltipGenerator(
                `${chartId}-tooltip`,
                pieChartTooltipInnerHtmlGenerator,
                false,
                DEFAULT_CHART_TOOLTIP_MARGIN,
                chartId
              )
            : undefined,
        },
      },
    };
    return (
      <Doughnut
        data={chartData as unknown as ChartData<"doughnut">}
        options={options}
      />
    );
  }, [items, hasData, theme, chartId, size, ringWidth]);

  const content = (
    <LoadingSpinnerOverlay
      isLoading={!!loading && hasData}
      backgroundColor={theme.colors.cardBgColor}
    >
      {(title || action) && (!isPhone || bare) && (
        <Header>
          <Title>{title}</Title>
          {action}
        </Header>
      )}
      <Body $compact={compact} $flush={hideLegend}>
        <Donut id={chartId} style={{ width: size, height: size }}>
          {chart}
          <Center>
            {pending ? (
              <Skeleton
                width={compact ? 56 : 72}
                height={compact ? 16 : 20}
                style={{ margin: "1px 0" }}
              />
            ) : (
              <DonutTotal total={total} compact={compact} />
            )}
            <TotalLabel>{t("labels.total")}</TotalLabel>
          </Center>
        </Donut>
        {pending && !hideLegend && (
          <Legend aria-busy="true">
            {LEGEND_SKELETON_WIDTHS.map((width) => (
              <LegendRow key={width}>
                <Skeleton width={6} height={6} radius={3} />
                <Skeleton width={width} height={10} />
                <Skeleton width={28} height={10} />
              </LegendRow>
            ))}
          </Legend>
        )}
        {!hasData && !loading && !hideLegend && (
          // Fills the legend's place, so an empty card still reads as a card.
          <EmptyNote>
            <EmptyTitle>
              {emptyTitle ?? t("empty.allocation_no_balance")}
            </EmptyTitle>
            <EmptyDescription>
              {emptyDescription ?? t("empty.allocation_no_balance_description")}
            </EmptyDescription>
          </EmptyNote>
        )}
        {hasData && !hideLegend && (
          <Legend>
            {items.map((item, index) => (
              <LegendRow key={`${item.name}-${index}`}>
                <Dot style={{ background: item.color }} />
                <Tooltip title={item.name} placement="topLeft">
                  <LegendName>{item.legendDisplay ?? item.name}</LegendName>
                </Tooltip>
                <LegendValue>{item.value}%</LegendValue>
              </LegendRow>
            ))}
          </Legend>
        )}
      </Body>
    </LoadingSpinnerOverlay>
  );

  if (isPhone && !bare)
    return (
      <MobileAllocation data-testid={MOBILE_ALLOCATION_ID}>
        <MobileAllocationHead>
          <MobileDetails
            $hasAction={!!action}
            onToggle={(event) => setExpanded(event.currentTarget.open)}
          >
            <summary>
              <span>{title ?? t("labels.total")}</span>
              <strong>
                {pending ? (
                  <Skeleton width={96} height={18} style={{ marginTop: 3 }} />
                ) : (
                  total
                )}
              </strong>
            </summary>
            {expanded && content}
          </MobileDetails>
          {action && <MobileAction>{action}</MobileAction>}
        </MobileAllocationHead>
      </MobileAllocation>
    );
  if (bare) return <BareLayout>{content}</BareLayout>;
  return <Card $compact={compact}>{content}</Card>;
};

export default React.memo(AllocationDonut);

const DonutTotal = ({
  total,
  compact,
}: Pick<AllocationDonutProps, "total" | "compact">): React.JSX.Element => {
  const valueRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const value = valueRef.current;
    if (!value?.parentElement) return;
    const availableWidth =
      value.parentElement.clientWidth - 2 * (RING_WIDTH + 12);
    setScale(Math.min(1, availableWidth / Math.max(value.scrollWidth, 1)));
  }, [total, compact]);
  return (
    <TotalValue
      ref={valueRef}
      data-testid={ALLOCATION_TOTAL_ID}
      $compact={compact}
      style={{ transform: `scale(${scale})` }}
    >
      {total}
    </TotalValue>
  );
};

const LEGEND_SKELETON_WIDTHS = [104, 84, 64];

const cardLayout = css`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const Card = styled(Surface)<{ $compact?: boolean }>`
  ${cardLayout};
  height: 100%;
  padding: ${(p) => (p.$compact ? "16px" : "20px")};
`;
const BareLayout = styled.div`
  ${cardLayout};
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 24px;
`;
const Title = styled.div`
  min-width: 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
// Grows to fill the card and centres the chart in it, so the space above and
// below the chart stays even whatever the card's height.
const Body = styled.div<{ $compact?: boolean; $flush?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  /* The chart and its legend sit as one centred group, so the card looks
     balanced however short or long the names are. Narrow cards (compact)
     tighten the gap and let the legend wrap under the chart. */
  justify-content: center;
  flex-wrap: ${(p) => (p.$compact ? "wrap" : "nowrap")};
  gap: ${(p) => (p.$compact ? "12px 16px" : "32px")};
  padding-top: ${(p) => (p.$flush ? 0 : 16)}px;
  min-width: 0;
`;
const Donut = styled.div`
  position: relative;
  flex: none;
`;
const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 ${RING_WIDTH + 12}px;
  pointer-events: none;
  text-align: center;
`;
const TotalValue = styled.div<{ $compact?: boolean }>`
  flex: none;
  width: max-content;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 18px;
  line-height: 1.2;
  font-weight: 560;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
    color: inherit !important;
  }
`;
const TotalLabel = styled.div`
  margin-top: 2px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 11px;
`;
// One grid for all rows, so names and values line up in two clean columns.
// Sized to its content (not stretched), so each value sits near its name.
const Legend = styled.ul`
  flex: 0 1 auto;
  min-width: 120px;
  max-width: 280px;
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: 6px minmax(0, max-content) auto;
  grid-auto-rows: 18px;
  column-gap: 10px;
  /* A little more air before the values than after the dots. */
  > li > span:last-child {
    margin-left: 14px;
  }
  row-gap: 10px;
  align-items: center;
`;
const LegendRow = styled.li`
  display: contents;
`;
const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
`;
const LegendName = styled.span`
  min-width: 0;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  font-size: 13px;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const LegendValue = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  text-align: right;
`;
const EmptyNote = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const EmptyTitle = styled.div`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14px;
  line-height: 20px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
`;
const EmptyDescription = styled.div`
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  line-height: 18px;
`;

const MobileAllocation = styled(Surface)`
  padding: 10px 12px;
`;
const MobileAllocationHead = styled.div`
  position: relative;
`;
const MobileAction = styled.div`
  position: absolute;
  right: 0;
  top: 0;
`;
const MobileDetails = styled.details<{ $hasAction: boolean }>`
  flex: 1;
  min-width: 0;
  summary {
    cursor: pointer;
    padding-right: ${(p) => (p.$hasAction ? 100 : 0)}px;
    min-height: 44px;
    color: ${(p) => p.theme.colors.secondaryText};
    font-size: 12px;
    strong {
      display: block;
      font-size: 18px;
      color: ${(p) => p.theme.colors.titleColor};
    }
  }
`;
